import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { CSVUploadModal } from './Master';
import HealthChecks from './HealthChecks';
import Routines from './Routines';
import DictateButton from '../components/DictateButton';

// ─── Badges ───────────────────────────────────────────────────────────────────
// Same colour language as the Meeting Points tracker, so a status reads the
// same wherever it appears.
const STATUS_COLOR = {
  'Pending':          { bg:'#4285F4', fg:'#ffffff' },   // waiting to be picked up
  'Assigned':         { bg:'#FF9900', fg:'#3a2200' },   // owned, not started
  'Accepted':         { bg:'#F6B26B', fg:'#3a2200' },   // owner acknowledged
  'In Progress':      { bg:'#FFD966', fg:'#3d2f00' },
  'Waiting':          { bg:'#D5A6BD', fg:'#3d1030' },   // blocked on user/vendor/approval
  'Needs Discussion': { bg:'#B4A7D6', fg:'#231a3d' },
  'Discussed':        { bg:'#A2C4C9', fg:'#0f2b2f' },
  'Escalated':        { bg:'#E06666', fg:'#ffffff' },   // raised up the chain
  'Completed':        { bg:'#93C47D', fg:'#12300a' },
  'Closed':           { bg:'#6AA84F', fg:'#ffffff' },
};
const PRIORITY_COLOR = {
  High:   { bg:'#F4CCCC', fg:'#7f1d1d' },
  Normal: { bg:'#FFF2CC', fg:'#7c5800' },
  Low:    { bg:'#D9EAD3', fg:'#274e13' },
};
const pill = (text, map) => {
  const c = map[text];
  if (!text) return <span style={{ color:'var(--text3)' }}>—</span>;
  if (!c) return <span className="badge">{text}</span>;
  return (
    <span style={{ background:c.bg, color:c.fg, fontSize:11, fontWeight:600,
      padding:'3px 10px', borderRadius:12, whiteSpace:'nowrap', display:'inline-block' }}>{text}</span>
  );
};
function statusBadge(s)   { return pill(s, STATUS_COLOR); }
function priorityBadge(p) { return pill(p, PRIORITY_COLOR); }

// ─── Onboarding checklists ────────────────────────────────────────────────────
// Mirrors the server rule: these categories can't be completed until every
// step is ticked. The server re-checks, so this is a convenience, not the gate.
const ONBOARDING = {
  'New Staff':   ['Mail ID', 'Groups', 'ERP', 'Class Access', 'Storage and Share'],
  'New Student': ['RDP User Account', 'Google Account', 'ERP Account', 'MyConcern'],
};
function OnboardingChecklist({ catName, value, onChange, readOnly }) {
  const items = ONBOARDING[catName];
  if (!items) return null;
  const v = value || {};
  const done = items.filter(i => v[i]).length;
  const allDone = done === items.length;
  return (
    <div style={{ marginBottom:14, padding:'12px 14px', borderRadius:'var(--radius)',
      background: allDone ? 'rgba(147,196,125,0.12)' : 'rgba(255,153,0,0.10)',
      border:`1px solid ${allDone ? '#93C47D' : '#FF9900'}` }}>
      <div style={{ fontSize:13, fontWeight:600, marginBottom:8 }}>
        📋 {catName} checklist — {done}/{items.length} done
        {!allDone && <span style={{ fontWeight:400, color:'var(--text2)' }}> · all steps required before completing</span>}
      </div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:'8px 18px' }}>
        {items.map(i => (
          <label className="opt" key={i} style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:13,
            cursor: readOnly ? 'default' : 'pointer' }}>
            <input type="checkbox" checked={!!v[i]} disabled={readOnly}
              onChange={() => onChange({ ...v, [i]: !v[i] })} />
            {i}
          </label>
        ))}
      </div>
    </div>
  );
}

// ─── Complete Task Modal (with optional asset replacement) ─────────────────────
function CompleteTaskModal({ task, stockAssets, onComplete, onClose, busy }) {
  // Pre-fill from any resolution already captured on the task (detail card /
  // prior edit) so it doesn't look empty and re-demand a resolution.
  const [resolution, setResolution] = useState(task.resolution || task.meeting_notes || '');
  const [replaced,   setReplaced]   = useState(false);
  const [replId,     setReplId]     = useState('');
  const [notes,      setNotes]      = useState('');
  const [resolveIncident, setResolveIncident] = useState(true);
  const [err,        setErr]        = useState('');
  const [invItems,  setInvItems]   = useState([]);
  const [consume,   setConsume]    = useState([]);   // [{consumable_id, quantity}]
  // Onboarding tasks capture the person's details here; they also write Master Data.
  const onboardCat = ONBOARDING[task.category_name] ? task.category_name : null;
  const [det, setDet] = useState({ name:'', email:'', groups:'', grade:'', section:'', id_number:'' });
  // Group IDs configured for this campus (Master Data → Group IDs)
  const [campusGroups, setCampusGroups] = useState([]);
  useEffect(() => {
    if (onboardCat === 'New Staff' && task.campus_id)
      api.get(`/master/groups/${task.campus_id}`).then(r => setCampusGroups(r.data)).catch(()=>{});
  }, [onboardCat, task.campus_id]);
  const groupList = (det.groups || '').split(',').map(s => s.trim()).filter(Boolean);
  const toggleGroup = label => {
    const next = groupList.includes(label) ? groupList.filter(g => g !== label) : [...groupList, label];
    setDet(d => ({ ...d, groups: next.join(', ') }));
  };

  const hasAsset = !!task.asset_id;
  const hasIncident = !!task.incident_id;

  useEffect(() => {
    if (task.campus_id) api.get('/consumables', { params:{ campus_id: task.campus_id } }).then(r => setInvItems(r.data)).catch(()=>{});
  }, [task.campus_id]);

  const submit = () => {
    if (onboardCat === 'New Staff') {
      if (!det.name.trim())  { setErr('Staff name is required'); return; }
      if (!det.email.trim()) { setErr('Mail ID is required'); return; }
    } else if (onboardCat === 'New Student') {
      if (!det.name.trim())      { setErr('Student name is required'); return; }
      if (!det.id_number.trim()) { setErr('ID Number is required'); return; }
    }
    // Onboarding tasks don't need a resolution — the checklist + details are the record
    if (!onboardCat && !resolution.trim()) { setErr('Resolution is required to complete the task'); return; }
    if (replaced && !replId) { setErr('Select the replacement asset from stock'); return; }
    onComplete({
      resolution: resolution.trim(),
      replace: replaced,
      replacement_asset_id: replaced ? parseInt(replId) : undefined,
      replacement_notes: notes || undefined,
      resolve_incident: hasIncident ? resolveIncident : undefined,
      consumables: consume.filter(c => c.consumable_id && parseFloat(c.quantity) > 0),
      details: onboardCat ? det : undefined,
    });
  };

  const sel = stockAssets.find(a => String(a.id) === replId);

  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && onClose()}>
      <div className="modal" style={{ maxWidth:560 }}>
        <div className="modal-header">
          <span className="modal-title">Complete Task — <span className="mono" style={{ color:'var(--accent)' }}>{task.task_id}</span></span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {/* Onboarding: capture who was set up — this also writes Master Data */}
        {onboardCat && (
          <div style={{ marginBottom:14, padding:'12px 14px', borderRadius:'var(--radius)',
            background:'var(--bg3)', border:'1px solid var(--accent)' }}>
            <div style={{ fontSize:13, fontWeight:600, marginBottom:2 }}>
              {onboardCat === 'New Staff' ? '👤 Staff details' : '🎓 Student details'}
            </div>
            <div style={{ fontSize:11, color:'var(--text2)', marginBottom:10 }}>
              Saved to Master Data ({onboardCat === 'New Staff' ? 'Staff Details' : 'Student Details'}) when you complete.
            </div>
            {onboardCat === 'New Staff' ? (
              <>
                <div className="form-row">
                  <div className="form-group"><label>Staff Name *</label>
                    <input value={det.name} onChange={e => setDet(d => ({ ...d, name:e.target.value }))} placeholder="e.g. Rajesh Kumar" /></div>
                  <div className="form-group"><label>Mail ID *</label>
                    <input type="email" value={det.email} onChange={e => setDet(d => ({ ...d, email:e.target.value }))} placeholder="name@tipsglobeducate.com" /></div>
                </div>
                <div className="form-group">
                  <label>Groups added <span style={{ fontSize:11, color:'var(--text2)' }}>— this campus's group IDs</span></label>
                  {campusGroups.length === 0 ? (
                    <input value={det.groups} onChange={e => setDet(d => ({ ...d, groups:e.target.value }))}
                      placeholder="No group IDs set up for this campus yet — add them under Master Data → Group IDs" />
                  ) : (
                    <div style={{ display:'flex', flexWrap:'wrap', gap:'6px 14px', padding:'8px 10px',
                      background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:'var(--radius)' }}>
                      {campusGroups.map(g => {
                        const label = g.group_email || g.name;
                        const on = groupList.includes(label);
                        return (
                          <label className="opt" key={g.id} style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:13 }}>
                            <input type="checkbox" checked={on} onChange={() => toggleGroup(label)} />
                            {g.name}{g.group_email && <span style={{ color:'var(--text2)', fontSize:11 }}> · {g.group_email}</span>}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="form-row">
                  <div className="form-group"><label>Student Name *</label>
                    <input value={det.name} onChange={e => setDet(d => ({ ...d, name:e.target.value }))} placeholder="e.g. Arjun S" /></div>
                  <div className="form-group"><label>ID Number *</label>
                    <input value={det.id_number} style={{ textTransform:'uppercase' }}
                      onChange={e => setDet(d => ({ ...d, id_number:e.target.value.toUpperCase() }))} placeholder="e.g. IB-12-123" /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Grade</label>
                    <input value={det.grade} onChange={e => setDet(d => ({ ...d, grade:e.target.value }))} placeholder="e.g. 6" /></div>
                  <div className="form-group"><label>Section</label>
                    <input value={det.section} onChange={e => setDet(d => ({ ...d, section:e.target.value }))} placeholder="e.g. A" /></div>
                </div>
              </>
            )}
          </div>
        )}

        <div className="form-group">
          <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', gap:8, flexWrap:'wrap' }}>
            <label style={{ marginBottom:0 }}>Resolution {onboardCat ? <span style={{ fontSize:11, color:'var(--text2)' }}>— optional</span> : '*'}</label>
            {/* Dictation for notes written on the move. Appends, so speaking
                never wipes what was already typed. */}
            <DictateButton onText={said =>
              setResolution(r => (r ? `${r.replace(/\s+$/, '')} ${said}` : said))} />
          </div>
          <textarea rows={onboardCat ? 2 : 3} value={resolution} onChange={e => setResolution(e.target.value)}
            placeholder={onboardCat ? 'Any notes? (left blank, it records the completed checklist)' : 'Describe how the issue was resolved…'} />
        </div>

        {hasIncident && (
          <div style={{ border:'1px solid rgba(248,113,113,0.3)', borderRadius:'var(--radius)', padding:12, background:'rgba(248,113,113,0.04)', marginBottom:8 }}>
            <label className="opt" style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', textTransform:'none', letterSpacing:0, fontWeight:600, fontSize:13 }}>
              <input type="checkbox" checked={resolveIncident} onChange={e => setResolveIncident(e.target.checked)}
                style={{ width:16, height:16, accentColor:'var(--red)' }} />
              🚨 Is the linked incident also resolved?
            </label>
            <div style={{ fontSize:11, color:'var(--text2)', marginTop:6 }}>
              {resolveIncident ? 'The incident will be marked Resolved when you close this task.' : 'The incident stays open; only the task closes (monitoring continues).'}
            </div>
          </div>
        )}

        {/* Consume inventory (optional) */}
        {invItems.length > 0 && (
          <div style={{ border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:12, marginBottom:8 }}>
            <div style={{ fontSize:13, fontWeight:600, marginBottom:8 }}>📦 Consume Inventory <span style={{ fontWeight:400, color:'var(--text2)', fontSize:11 }}>(optional)</span></div>
            {consume.map((c, i) => (
              <div key={i} className="flex-gap" style={{ gap:8, marginBottom:6 }}>
                <select value={c.consumable_id} onChange={e => setConsume(cs => cs.map((x,j)=>j===i?{...x,consumable_id:e.target.value}:x))} style={{ flex:1 }}>
                  <option value="">— Item —</option>
                  {invItems.map(it => <option key={it.id} value={it.id}>{it.name} ({(+it.current_stock).toFixed(0)} {it.unit_type})</option>)}
                </select>
                <input type="number" placeholder="Qty" value={c.quantity} onChange={e => setConsume(cs => cs.map((x,j)=>j===i?{...x,quantity:e.target.value}:x))} style={{ width:80 }} />
                <button className="btn btn-secondary btn-sm" onClick={() => setConsume(cs => cs.filter((_,j)=>j!==i))}>×</button>
              </div>
            ))}
            <button className="btn btn-secondary btn-sm" onClick={() => setConsume(cs => [...cs, { consumable_id:'', quantity:'' }])}>+ Add item used</button>
          </div>
        )}

        {hasAsset && (
          <div style={{ border:'2px solid rgba(245,191,36,0.4)', borderRadius:'var(--radius)', padding:14, background:'rgba(245,191,36,0.04)', marginBottom:8 }}>
            <label className="opt" style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', marginBottom: replaced ? 14 : 0, textTransform:'none', letterSpacing:0, fontWeight:600, fontSize:13 }}>
              <input type="checkbox" checked={replaced}
                onChange={e => { setReplaced(e.target.checked); setReplId(''); }}
                style={{ width:16, height:16, accentColor:'var(--amber)' }} />
              🔄 Asset was replaced during resolution
            </label>

            {replaced && (
              <>
                {/* Faulty asset */}
                <div style={{ background:'rgba(248,113,113,0.08)', border:'1px solid rgba(248,113,113,0.2)', borderRadius:'var(--radius)', padding:10, marginBottom:12 }}>
                  <div className="flex-gap" style={{ justifyContent:'space-between' }}>
                    <div>
                      <span className="mono" style={{ color:'var(--red)', fontWeight:700, fontSize:13 }}>{task.asset_tag}</span>
                      <span style={{ fontSize:12, color:'var(--text2)', marginLeft:8 }}>{task.asset_type} · {task.asset_brand}{task.asset_model ? ` ${task.asset_model}` : ''}</span>
                    </div>
                    <span style={{ fontSize:11, color:'var(--red)' }}>→ Sent for Service</span>
                  </div>
                  {task.asset_room_name && <div style={{ fontSize:11, color:'var(--text2)', marginTop:4 }}>📍 Will be removed from: {task.asset_room_name}</div>}
                  {task.asset_staff_name && <div style={{ fontSize:11, color:'var(--text2)', marginTop:4 }}>👤 Will be unassigned from: {task.asset_staff_name}</div>}
                </div>

                {/* Replacement picker */}
                <div className="form-group" style={{ marginBottom:10 }}>
                  <label>
                    Replacement Asset from Stock
                    <span style={{ fontSize:11, color:'var(--text2)', fontWeight:400, textTransform:'none' }}>
                      {' '}— {task.asset_type}, same campus
                      {stockAssets.length === 0 && <span style={{ color:'var(--red)' }}> · No stock available for this type</span>}
                    </span>
                  </label>
                  <select value={replId} onChange={e => setReplId(e.target.value)}>
                    <option value="">— Select replacement —</option>
                    {stockAssets.map(a => (
                      <option key={a.id} value={a.id}>
                        {a.asset_tag} · {a.brand} {a.model}{a.serial_number ? ` · S/N: ${a.serial_number}` : ''}
                      </option>
                    ))}
                  </select>
                  {sel && (
                    <div style={{ marginTop:6, background:'rgba(52,211,153,0.08)', border:'1px solid rgba(52,211,153,0.2)', borderRadius:'var(--radius)', padding:10, fontSize:12 }}>
                      <div className="flex-gap" style={{ justifyContent:'space-between' }}>
                        <span className="mono" style={{ color:'var(--green)', fontWeight:700 }}>{sel.asset_tag}</span>
                        <span style={{ fontSize:11, color:'var(--green)' }}>→ Will move to {task.asset_room_name || task.asset_staff_name || 'this location'}</span>
                      </div>
                      <div style={{ color:'var(--text2)', marginTop:4 }}>{sel.brand} {sel.model}{sel.specifications ? ` · ${sel.specifications}` : ''}</div>
                    </div>
                  )}
                </div>

                <div className="form-group" style={{ marginBottom:0 }}>
                  <label>Service Notes <span style={{ fontSize:11, color:'var(--text2)' }}>(optional)</span></label>
                  <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Scroll wheel defective, under warranty" />
                </div>
              </>
            )}
          </div>
        )}

        {err && <div className="err-msg">{err}</div>}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={busy}
            style={{ background:'var(--green)', borderColor:'var(--green)' }}>
            {busy ? 'Completing…' : '✅ Complete Task'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Task Detail / Edit Page ──────────────────────────────────────────────────
function TaskDetail({ taskId, onBack, onSaved }) {
  const { user, isAdmin, can } = useAuth();
  const [task,        setTask]        = useState(null);
  const [rooms,       setRooms]       = useState([]);
  const [categories,  setCategories]  = useState([]);
  const [informedBy,  setInformedBy]  = useState([]);
  const [campusAssets,setCampusAssets]= useState([]);
  const [stockAssets, setStockAssets] = useState([]);  // available stock for replacement
  const [replacementHistory, setReplacementHistory] = useState([]);
  const [completeModal, setCompleteModal] = useState(false);
  const [infSearch,   setInfSearch]   = useState('');
  const [infOpen,     setInfOpen]     = useState(false);   // staff suggestion list open?
  const [form,        setForm]        = useState(null);
  const [onboarding,  setOnboarding]  = useState(null);   // New Staff / New Student checklist
  const [updates,     setUpdates]     = useState([]);
  const [progressNote, setProgressNote] = useState('');
  const [showProgress, setShowProgress] = useState(false);
  const [busy,        setBusy]        = useState(false);
  const [err,         setErr]         = useState('');
  const [loading,     setLoading]     = useState(true);
  const [history,     setHistory]     = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const tk = (await api.get(`/tasks/${taskId}`)).data;
      const rtype = tk.reporter_type === 'student' ? 'student' : 'staff';
      const cats = await api.get('/master/categories', { params:{ type: rtype } });
      setTask(tk);
      setOnboarding(tk.onboarding || {});
      setCategories(cats.data);
      api.get(`/tasks/${taskId}/history`).then(r => setHistory(r.data)).catch(() => setHistory([]));
      api.get(`/tasks/${taskId}/updates`).then(r => setUpdates(r.data)).catch(() => setUpdates([]));
      setInfSearch(tk.student_name || tk.informed_by_display || tk.informed_by_name || '');
      setForm({
        room_id:          String(tk.room_id || ''),
        category_id:      String(tk.category_id || ''),
        asset_id:         String(tk.asset_id || ''),
        title:            tk.title || '',
        description:      tk.description || '',
        priority:         tk.priority || 'Normal',
        informed_by_id:   String(tk.informed_by_id || ''),
        informed_by_name: tk.informed_by_name || '',
        discuss_in_meeting: tk.discuss_in_meeting || false,
        meeting_date:     tk.meeting_date ? tk.meeting_date.slice(0,10) : '',
        meeting_notes:    tk.meeting_notes || '',
        resolution:       tk.resolution || ''
      });
      // Load campus-specific data
      if (tk.campus_id) {
        const [r, ib, a, rep] = await Promise.all([
          api.get(`/master/rooms/${tk.campus_id}`),
          api.get(`/master/informedby/${tk.campus_id}`),
          api.get('/assets', { params: { campus_id: tk.campus_id } }),
          api.get(`/tasks/${taskId}/replacement`)
        ]);
        setRooms(r.data.filter(x => x.active));
        setInformedBy(ib.data.filter(x => x.active));
        setCampusAssets(a.data);
        setReplacementHistory(rep.data);
        // Load stock assets of same type for replacement dropdown
        if (tk.asset_type) {
          const stock = await api.get('/assets', { params: { campus_id: tk.campus_id, status: 'stock', asset_type: tk.asset_type } });
          setStockAssets(stock.data);
        }
      }
    } catch(e) { setErr('Failed to load task'); }
    setLoading(false);
  }, [taskId]);

  useEffect(() => { load(); }, [load]);

  const canEdit = task && (isAdmin || can('tasks','edit'));
  // New Staff / New Student: no description, no asset — the checklist and the
  // captured person details are the record.
  const isOnboardingTask = !!(task && ONBOARDING[task.category_name]);

  // Save detail edits (does NOT complete the task)
  const save = async () => {
    if (!form.title.trim()) { setErr('Title is required'); return; }
    if (!(infSearch || form.informed_by_name).trim()) { setErr('Reported By (Staff) is required'); return; }
    setBusy(true); setErr('');
    try {
      await api.put(`/tasks/${taskId}`, {
        ...form,
        room_id:      form.room_id      ? parseInt(form.room_id)      : null,
        category_id:  form.category_id  ? parseInt(form.category_id)  : null,
        asset_id:     form.asset_id     ? parseInt(form.asset_id)      : null,
        informed_by_id: form.informed_by_id ? parseInt(form.informed_by_id) : null,
        informed_by_name: infSearch || form.informed_by_name
      });
      await load();
      onSaved?.();
    } catch(e) { setErr(e.response?.data?.error || 'Save failed'); }
    finally { setBusy(false); }
  };

  // Complete the task (with optional replacement) via the /close endpoint
  const doComplete = async payload => {
    setBusy(true); setErr('');
    try {
      // Persist any pending detail edits first
      await api.put(`/tasks/${taskId}`, {
        ...form,
        room_id:      form.room_id      ? parseInt(form.room_id)      : null,
        category_id:  form.category_id  ? parseInt(form.category_id)  : null,
        asset_id:     form.asset_id     ? parseInt(form.asset_id)      : null,
        informed_by_id: form.informed_by_id ? parseInt(form.informed_by_id) : null,
        informed_by_name: infSearch || form.informed_by_name
      });
      await api.post(`/tasks/${taskId}/close`, payload);
      setCompleteModal(false);
      await load();
      onSaved?.();
    } catch(e) { setErr(e.response?.data?.error || 'Complete failed'); }
    finally { setBusy(false); }
  };

  // Auto-fill room when asset is selected
  const handleAssetChange = val => {
    setForm(f => ({ ...f, asset_id: val }));
    if (val) {
      const asset = campusAssets.find(a => String(a.id) === val);
      if (asset?.room_id && !form.room_id) {
        setForm(f => ({ ...f, asset_id: val, room_id: String(asset.room_id) }));
      }
    }
  };

  const filteredInformed = informedBy.filter(p =>
    !infSearch || p.name.toLowerCase().includes(infSearch.toLowerCase())
  );

  const refresh = async () => { await load(); onSaved?.(); };
  const doAssign = async () => { setBusy(true); try { await api.post(`/tasks/${taskId}/assign`); await refresh(); } catch(e){ setErr(e.response?.data?.error||'Failed'); } finally { setBusy(false); } };
  const doStatus = async (status, extra={}) => { setBusy(true); try { await api.post(`/tasks/${taskId}/status`, { status, ...extra }); await refresh(); } catch(e){ setErr(e.response?.data?.error||'Failed'); } finally { setBusy(false); } };
  // Ticking a step saves immediately — no separate Save for the checklist
  const saveOnboarding = async v => {
    setOnboarding(v);                                   // optimistic
    try { await api.put(`/tasks/${taskId}/onboarding`, { onboarding: v }); }
    catch (e) { setErr(e.response?.data?.error || 'Could not save the checklist'); refresh(); }
  };
  const doProgress = async () => {
    if (!progressNote.trim()) return;
    setBusy(true);
    try { await api.post(`/tasks/${taskId}/progress`, { note: progressNote.trim() }); setProgressNote(''); setShowProgress(false); await refresh(); }
    catch(e){ setErr(e.response?.data?.error||'Failed'); } finally { setBusy(false); }
  };
  const doVerify = async () => {
    if (!window.confirm('Verify this task as resolved and close it?')) return;
    setBusy(true);
    try { await api.post(`/tasks/${taskId}/verify`); await refresh(); } catch(e){ setErr(e.response?.data?.error||'Failed'); } finally { setBusy(false); }
  };

  const age = task ? Math.floor((Date.now() - new Date(task.created_at)) / 86400000) : 0;
  const showDiscuss = task && (age >= 1 || form?.discuss_in_meeting);

  if (loading) return <div style={{ padding:40, textAlign:'center', color:'var(--text2)' }}>Loading…</div>;
  if (!task || !form) return <div style={{ padding:40 }}>Task not found.</div>;

  const selectedAsset = campusAssets.find(a => String(a.id) === form.asset_id);

  return (
    <div>
      {/* Back + header */}
      <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:24 }}>
        <button className="btn btn-secondary btn-sm" onClick={onBack}>← Back</button>
        <div style={{ flex:1 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
            <span className="mono" style={{ color:'var(--accent)', fontWeight:700, fontSize:15 }}>{task.task_id}</span>
            {statusBadge(task.status)}
            {priorityBadge(task.priority)}
          </div>
          <div style={{ fontSize:12, color:'var(--text2)', marginTop:4 }}>
            {task.campus_name}
            {task.room_name && <> · {task.room_name}</>}
            {' · '}Created {new Date(task.created_at).toLocaleDateString()} by {task.created_by_name}
            {task.updated_by_name && <> · Updated {new Date(task.updated_at).toLocaleDateString()} by {task.updated_by_name}</>}
          </div>
        </div>
        {canEdit && (
          <div className="flex-gap" style={{ gap:8 }}>
            <button className="btn btn-secondary" onClick={save} disabled={busy}>
              {busy ? 'Saving…' : 'Save Changes'}
            </button>
            {!['Assigned','Accepted','In Progress','Waiting','Escalated','Completed','Closed'].includes(task.status) && (
              <button className="btn btn-primary" onClick={doAssign} disabled={busy}>📌 Assign to me</button>
            )}
            {['Assigned','In Progress','Accepted','Waiting','Escalated'].includes(task.status) && (
              <button className="btn btn-secondary" onClick={() => setShowProgress(true)} disabled={busy}>➕ Add Update</button>
            )}
            {/* Lifecycle transitions */}
            {task.status === 'Assigned' && (
              <button className="btn btn-secondary" onClick={() => doStatus('Accepted')} disabled={busy}>👍 Accept</button>
            )}
            {['Assigned','Accepted','Waiting','Escalated'].includes(task.status) && (
              <button className="btn btn-secondary" onClick={() => doStatus('In Progress')} disabled={busy}>▶ Start</button>
            )}
            {['Assigned','Accepted','In Progress'].includes(task.status) && (
              <button className="btn btn-secondary" onClick={() => { const w = window.prompt('Waiting on? (User / Vendor / Approval)', 'User'); if (w) doStatus('Waiting', { waiting_on: w }); }} disabled={busy}>⏸ Waiting</button>
            )}
            {['Assigned','Accepted','In Progress','Waiting'].includes(task.status) && (
              <button className="btn btn-secondary" onClick={() => { const n = window.prompt('Escalation reason (optional):', ''); doStatus('Escalated', { note: n || '' }); }} disabled={busy}
                style={{ color:'var(--red)' }}>⏫ Escalate</button>
            )}
            {task.status !== 'Completed' && task.status !== 'Closed' && (
              <button className="btn btn-primary" onClick={() => setCompleteModal(true)} disabled={busy}
                style={{ background:'var(--green)', borderColor:'var(--green)' }}>
                ✅ Complete Task
              </button>
            )}
            {task.status === 'Completed' && (
              <button className="btn btn-primary" onClick={doVerify} disabled={busy}
                style={{ background:'var(--green)', borderColor:'var(--green)' }}>✔ Verify &amp; Close</button>
            )}
          </div>
        )}
      </div>

      {/* Onboarding steps — ticked here as IT completes each account/access */}
      <OnboardingChecklist catName={task.category_name} value={onboarding}
        readOnly={!canEdit || ['Completed','Closed'].includes(task.status)}
        onChange={saveOnboarding} />

      <div style={{ display:'grid', gridTemplateColumns:'1fr 340px', gap:20, alignItems:'start' }}>
        {/* ── Left column ── */}
        <div>
          {/* Task Details card */}
          <div className="card" style={{ marginBottom:16 }}>
            <div style={{ fontWeight:600, fontSize:13, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:14 }}>Task Details</div>

            <div className="form-group">
              <label>Title</label>
              {canEdit
                ? <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
                : <div style={{ padding:'8px 0', fontWeight:500 }}>{task.title}</div>}
            </div>

            {/* Onboarding tasks carry no description — the checklist and the
                captured details are the record. */}
            {!isOnboardingTask && (
              <div className="form-group">
                <label>Description</label>
                {canEdit
                  ? <textarea value={form.description} rows={3} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Additional details…" />
                  : <div style={{ padding:'8px 0', color: task.description ? 'var(--text)' : 'var(--text2)', fontSize:13 }}>{task.description || '—'}</div>}
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>Category</label>
                {canEdit
                  ? <select value={form.category_id} onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}>
                      <option value="">— Select —</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  : <div style={{ padding:'8px 0', fontSize:13 }}>{task.category_name || '—'}</div>}
              </div>
              <div className="form-group">
                <label>Priority</label>
                {canEdit
                  ? <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                      <option>Normal</option><option>High</option><option>Low</option>
                    </select>
                  : priorityBadge(task.priority)}
              </div>
            </div>

            {/* Onboarding tasks have no room / reporter — hidden here too */}
            <div className="form-row" style={{ display: isOnboardingTask ? 'none' : undefined }}>
              <div className="form-group">
                <label>Room</label>
                {canEdit
                  ? <select value={form.room_id} onChange={e => setForm(f => ({ ...f, room_id: e.target.value }))}>
                      <option value="">— None —</option>
                      {rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  : <div style={{ padding:'8px 0', fontSize:13 }}>{task.room_name || '—'}</div>}
              </div>
              <div className="form-group" style={{ position:'relative' }}>
                <label>Reported By ({task.reporter_type === 'student' ? 'Student' : 'Staff'})</label>
                {task.reporter_type === 'student' ? (
                  <div style={{ padding:'8px 0', fontSize:13 }}>
                    {task.student_name || task.informed_by_name || '—'}
                    {(task.student_grade || task.student_section || task.student_id_number) && (
                      <span style={{ color:'var(--text2)', fontSize:11 }}>
                        {task.student_id_number ? ` · ID ${task.student_id_number}` : ''}
                        {task.student_grade ? ` · Grade ${task.student_grade}` : ''}{task.student_section ? `-${task.student_section}` : ''}
                      </span>
                    )}
                  </div>
                ) : canEdit ? (
                  <>
                    <input value={infSearch}
                      onChange={e => { setInfSearch(e.target.value); setInfOpen(true); setForm(f => ({ ...f, informed_by_id:'', informed_by_name: e.target.value })); }}
                      onFocus={() => setInfOpen(true)}
                      onBlur={() => setTimeout(() => setInfOpen(false), 120)}
                      placeholder="Search staff…" autoComplete="off" />
                    {infOpen && infSearch && filteredInformed.length > 0 && (
                      <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:99, background:'var(--bg2)', border:'1px solid var(--accent)', borderRadius:'0 0 var(--radius) var(--radius)', maxHeight:140, overflowY:'auto', boxShadow:'0 8px 24px rgba(0,0,0,0.4)' }}>
                        {filteredInformed.map(p => (
                          <div key={p.id} onMouseDown={() => { setInfSearch(p.name); setInfOpen(false); setForm(f => ({ ...f, informed_by_id: String(p.id), informed_by_name: p.name })); }}
                            style={{ padding:'7px 12px', cursor:'pointer', fontSize:13, borderBottom:'1px solid var(--border)' }}
                            onMouseEnter={e => e.currentTarget.style.background='var(--bg3)'}
                            onMouseLeave={e => e.currentTarget.style.background=''}>
                            <div style={{ fontWeight:500 }}>{p.name}</div>
                            {p.designation && <div style={{ fontSize:11, color:'var(--text2)' }}>{p.designation}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ padding:'8px 0', fontSize:13 }}>
                    {task.informed_by_display || task.informed_by_name || '—'}
                    {task.informed_by_designation && <span style={{ color:'var(--text2)', fontSize:11 }}> · {task.informed_by_designation}</span>}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Meeting & Resolution card */}
          <div className="card">
            <div style={{ fontWeight:600, fontSize:13, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:14 }}>Meeting & Resolution</div>

            {showDiscuss && (
              <div style={{ background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:14, marginBottom:14 }}>
                {canEdit ? (
                  <label className="opt" style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', textTransform:'none', fontSize:13, letterSpacing:0, marginBottom: form.discuss_in_meeting ? 12 : 0, fontWeight:500 }}>
                    <input type="checkbox" checked={form.discuss_in_meeting}
                      onChange={e => setForm(f => ({ ...f, discuss_in_meeting: e.target.checked }))}
                      style={{ width:16, height:16 }} />
                    🗣 Discuss in meeting
                  </label>
                ) : (
                  <div style={{ fontSize:13, fontWeight:500, marginBottom: task.discuss_in_meeting ? 12 : 0 }}>
                    {task.discuss_in_meeting ? '🗣 Flagged for meeting discussion' : ''}
                  </div>
                )}
                {(form.discuss_in_meeting || task.discuss_in_meeting) && (
                  <>
                    <div className="form-group" style={{ marginBottom:10 }}>
                      <label>Meeting Date</label>
                      {canEdit
                        ? <input type="date" value={form.meeting_date} onChange={e => setForm(f => ({ ...f, meeting_date: e.target.value }))} />
                        : <div style={{ padding:'8px 0', fontSize:13 }}>{task.meeting_date ? new Date(task.meeting_date).toLocaleDateString() : '—'}</div>}
                    </div>
                    <div className="form-group" style={{ marginBottom:0 }}>
                      <label>Meeting Notes</label>
                      {canEdit
                        ? <textarea value={form.meeting_notes} rows={3} onChange={e => setForm(f => ({ ...f, meeting_notes: e.target.value }))} placeholder="What was discussed…" />
                        : <div style={{ padding:'8px 0', fontSize:13 }}>{task.meeting_notes || '—'}</div>}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Resolution (read-only display; completion happens via Complete Task button) */}
            <div style={{ background:'rgba(52,211,153,0.06)', border:'1px solid rgba(52,211,153,0.2)', borderRadius:'var(--radius)', padding:14 }}>
              <div className="flex-gap" style={{ justifyContent:'space-between', marginBottom:8 }}>
                <div style={{ fontSize:12, fontWeight:600, color:'var(--green)' }}>✅ Resolution</div>
                {canEdit && task?.status !== 'Completed' && (
                  <button className="btn btn-primary btn-sm" onClick={() => setCompleteModal(true)}
                    style={{ background:'var(--green)', borderColor:'var(--green)' }}>
                    Complete Task{task?.asset_id ? ' / Replace Asset' : ''}
                  </button>
                )}
              </div>
              <div style={{ padding:'4px 0', fontSize:13, color: task.resolution ? 'var(--text)' : 'var(--text2)' }}>
                {task.resolution || (task.status === 'Completed' ? '—' : 'Not resolved yet. Click "Complete Task" to close it.')}
              </div>
            </div>

            {/* Replacement history (if task was replaced before) */}
            {replacementHistory.length > 0 && (
              <div style={{ marginTop:14, background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:12 }}>
                <div style={{ fontSize:11, fontWeight:600, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:10 }}>Replacement History</div>
                {replacementHistory.map(r => (
                  <div key={r.id} style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, padding:'10px 0', borderBottom:'1px solid var(--border)' }}>
                    <div>
                      <div style={{ fontSize:11, color:'var(--text2)' }}>Removed</div>
                      <span className="mono" style={{ fontSize:12, color:'var(--red)' }}>{r.old_asset_tag}</span>
                      <span style={{ fontSize:11, color:'var(--text2)', marginLeft:6 }}>{r.old_asset_type}</span>
                    </div>
                    <div>
                      <div style={{ fontSize:11, color:'var(--text2)' }}>Replaced with</div>
                      <span className="mono" style={{ fontSize:12, color:'var(--green)' }}>{r.new_asset_tag}</span>
                      <span style={{ fontSize:11, color:'var(--text2)', marginLeft:6 }}>{r.new_asset_type}</span>
                    </div>
                    <div style={{ fontSize:11, color:'var(--text2)', gridColumn:'1/-1' }}>
                      {new Date(r.replacement_date).toLocaleDateString()} · by {r.technician_name}
                      {r.room_name && ` · 📍 ${r.room_name}`}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Right column ── */}
        <div>
          {/* Linked laptop (student tasks) */}
          {task.reporter_type === 'student' && (
            <div className="card" style={{ marginBottom:16 }}>
              <div style={{ fontWeight:600, fontSize:13, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:14 }}>💻 Linked Laptop</div>
              {task.laptop_serial || task.laptop_brand ? (
                <div style={{ fontSize:13 }}>
                  <div style={{ fontWeight:500 }}>{task.laptop_brand} {task.laptop_model}</div>
                  <div style={{ fontSize:12, color:'var(--text2)', marginTop:4 }}>Serial: <span className="mono">{task.laptop_serial || '—'}</span></div>
                  {task.laptop_charger && <div style={{ fontSize:12, color:'var(--text2)' }}>Charger: <span className="mono">{task.laptop_charger}</span></div>}
                  <div style={{ fontSize:12, color:'var(--text2)' }}>OS License: {task.laptop_os_license ? 'Yes' : 'No'}</div>
                </div>
              ) : <div style={{ color:'var(--text2)', fontSize:13 }}>No laptop linked</div>}
            </div>
          )}

          {/* Linked Asset card (staff tasks only; not for onboarding) */}
          {task.reporter_type !== 'student' && !isOnboardingTask && (
          <div className="card" style={{ marginBottom:16 }}>
            <div style={{ fontWeight:600, fontSize:13, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:14 }}>Linked Asset</div>
            {canEdit ? (
              <div className="form-group" style={{ marginBottom:0 }}>
                <label>Select Asset</label>
                <select value={form.asset_id} onChange={e => handleAssetChange(e.target.value)}>
                  <option value="">— No asset —</option>
                  {campusAssets.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.asset_tag} · {a.asset_type || a.brand}{a.room_name ? ` (${a.room_name})` : a.staff_name ? ` → ${a.staff_name}` : ''}
                    </option>
                  ))}
                </select>
                {selectedAsset && <AssetMiniCard asset={selectedAsset} />}
              </div>
            ) : task.asset_id ? (
              <AssetMiniCard asset={{
                asset_tag:    task.asset_tag,
                asset_type:   task.asset_type,
                asset_brand:  task.asset_brand,
                asset_model:  task.asset_model,
                asset_condition: task.asset_condition,
                room_name:    task.asset_room_name,
                staff_name:   task.asset_staff_name,
              }} />
            ) : (
              <div style={{ color:'var(--text2)', fontSize:13 }}>No asset linked</div>
            )}
          </div>
          )}

          {/* Quick info card */}
          <div className="card">
            <div style={{ fontWeight:600, fontSize:13, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:12 }}>Info</div>
            {[
              ['Status',   statusBadge(task.status)],
              ['Campus',   task.campus_name],
              ['Created',  new Date(task.created_at).toLocaleString()],
              ['By',       task.created_by_name],
              ...(task.status === 'Completed' ? [['Completed', new Date(task.updated_at).toLocaleString()]] : []),
              ['Age',      `${age} day${age !== 1 ? 's' : ''}`],
            ].map(([label, val]) => (
              <div key={label} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingBottom:8, marginBottom:8, borderBottom:'1px solid var(--border)' }}>
                <span style={{ fontSize:12, color:'var(--text2)' }}>{label}</span>
                <span style={{ fontSize:12, textAlign:'right' }}>{val}</span>
              </div>
            ))}
          </div>

          {/* Timeline */}
          <div className="card" style={{ marginTop:16 }}>
            <div style={{ fontWeight:600, fontSize:13, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:12 }}>Timeline</div>
            {!history.length ? <div style={{ fontSize:12, color:'var(--text2)' }}>No history recorded.</div> : (
              <div style={{ borderLeft:'2px solid var(--border)', paddingLeft:12 }}>
                {history.map((h, i) => (
                  <div key={i} style={{ marginBottom:10 }}>
                    <div style={{ fontSize:12, fontWeight:500 }}>
                      {h.action}{h.field_name ? ` · ${h.field_name}` : ''}
                      {h.old_value && h.new_value ? `: ${h.old_value} → ${h.new_value}` : h.new_value ? `: ${h.new_value}` : ''}
                    </div>
                    {h.remarks && <div style={{ fontSize:11, color:'var(--text2)' }}>{h.remarks}</div>}
                    <div style={{ fontSize:11, color:'var(--text3)' }}>{h.by_name || '—'} · {new Date(h.created_at).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Progress updates (visible to the requester) */}
          <div className="card" style={{ marginTop:16 }}>
            <div style={{ fontWeight:600, fontSize:13, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:12 }}>Progress Updates</div>
            {task.assigned_to_name && <div style={{ fontSize:12, color:'var(--text2)', marginBottom:8 }}>Assigned to <b>{task.assigned_to_name}</b>{task.verified_by_name && <> · Verified by <b>{task.verified_by_name}</b></>}</div>}
            {!updates.length ? <div style={{ fontSize:12, color:'var(--text2)' }}>No progress updates yet.</div> : (
              <div style={{ borderLeft:'2px solid var(--border)', paddingLeft:12 }}>
                {updates.map(u => (
                  <div key={u.id} style={{ marginBottom:10 }}>
                    <div style={{ fontSize:12, fontWeight:500 }}>{statusBadge(u.status_at)}</div>
                    {u.note && <div style={{ fontSize:12, marginTop:2 }}>{u.note}</div>}
                    <div style={{ fontSize:11, color:'var(--text3)' }}>{u.by_name || '—'} · {new Date(u.created_at).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {err && <div className="err-msg" style={{ marginTop:16 }}>{err}</div>}

      {showProgress && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && setShowProgress(false)}>
          <div className="modal" style={{ maxWidth:440 }}>
            <div className="modal-header"><span className="modal-title">Add Progress Update</span><button className="modal-close" onClick={() => setShowProgress(false)}>×</button></div>
            <div className="form-group"><label>Update note (visible to the requester)</label>
              <textarea rows={3} value={progressNote} onChange={e => setProgressNote(e.target.value)} placeholder="e.g. Diagnosed faulty RAM, ordered replacement…" /></div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowProgress(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={doProgress} disabled={busy || !progressNote.trim()}>{busy?'Posting…':'Post Update'}</button>
            </div>
          </div>
        </div>
      )}

      {completeModal && (
        <CompleteTaskModal
          task={task}
          stockAssets={stockAssets}
          onComplete={doComplete}
          onClose={() => setCompleteModal(false)}
          busy={busy}
        />
      )}
    </div>
  );
}

function AssetMiniCard({ asset }) {
  const condColors = { Good:'var(--green)', Fair:'var(--amber)', 'Needs Repair':'var(--orange)', Faulty:'var(--red)' };
  return (
    <div style={{ marginTop:10, background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:12 }}>
      <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <div className="mono" style={{ fontSize:13, color:'var(--accent)', fontWeight:600 }}>{asset.asset_tag}</div>
          <div style={{ fontSize:13, fontWeight:500, marginTop:2 }}>{asset.asset_type || '—'}</div>
          {(asset.brand || asset.asset_brand) && (
            <div style={{ fontSize:12, color:'var(--text2)' }}>{asset.brand || asset.asset_brand}{(asset.model || asset.asset_model) ? ` · ${asset.model || asset.asset_model}` : ''}</div>
          )}
        </div>
        {(asset.condition || asset.asset_condition) && (
          <span style={{ fontSize:11, color: condColors[asset.condition || asset.asset_condition] || 'var(--text2)', fontWeight:600 }}>
            ● {asset.condition || asset.asset_condition}
          </span>
        )}
      </div>
      {(asset.room_name || asset.asset_room_name) && (
        <div style={{ fontSize:11, color:'var(--text2)', marginTop:6 }}>📍 {asset.room_name || asset.asset_room_name}</div>
      )}
      {(asset.staff_name || asset.asset_staff_name) && (
        <div style={{ fontSize:11, color:'var(--text2)', marginTop:2 }}>👤 {asset.staff_name || asset.asset_staff_name}</div>
      )}
    </div>
  );
}

// ─── Task List Page ───────────────────────────────────────────────────────────
const emptyForm = {
  campus_id:'', room_id:'', category_id:'', asset_id:'', title:'', description:'',
  priority:'Normal', informed_by_id:'', informed_by_name:'', assigned_to:'', onboarding:{},
  reporter_type:'staff', task_kind:'staff', student_id:'', student_laptop_id:'',
  discuss_in_meeting:false, meeting_date:'', meeting_notes:'', resolution:'',
  due_date:''            // IT tasks only
};

export default function Tasks({ go, arg, clearArg }) {
  const { user, isAdmin, can } = useAuth();
  const [tab,          setTab]          = useState('tasks');  // healthcheck | routines | tasks
  const [tasks,        setTasks]        = useState([]);
  const [campuses,     setCampuses]     = useState([]);
  const [rooms,        setRooms]        = useState([]);
  const [staffCats,    setStaffCats]    = useState([]);
  const [studentCats,  setStudentCats]  = useState([]);
  const [itCats,       setItCats]       = useState([]);   // IT Task / IT Training
  const [informedBy,   setInformedBy]   = useState([]);
  const [students,     setStudents]     = useState([]);
  const [studentLaptops, setStudentLaptops] = useState([]);  // laptops of the chosen student
  const [campusAssets, setCampusAssets] = useState([]);  // all assets for campus
  const [infSearch,    setInfSearch]    = useState('');   // reporter search (staff or student)
  const [filters,     setFilters]     = useState({ search:'', status:'', priority:'', campus_id:'', discuss:'', open:'', assigned_to:'', kind:'' });
  const [assignees,   setAssignees]   = useState([]);   // IT people with tickets (By Person filter)
  const [itUsers,     setItUsers]     = useState([]);   // IT people a ticket can be assigned to
  const [itAssignees, setItAssignees] = useState([]);   // IT Task: ids of everyone it goes to
  const [infOpen,     setInfOpen]     = useState(false); // reporter suggestion list open?
  const [newStaff,    setNewStaff]    = useState(null); // quick-add staff form: null | {name,email}
  const [staffBusy,   setStaffBusy]   = useState(false);
  const [staffErr,    setStaffErr]    = useState('');
  const [newRoom,     setNewRoom]     = useState(null); // quick-add room: null | {name,code}
  const [roomBusy,    setRoomBusy]    = useState(false);
  const [roomErr,     setRoomErr]     = useState('');
  const [newStudent,  setNewStudent]  = useState(null); // quick-add student: null | {name,id_number}
  const [studentBusy, setStudentBusy] = useState(false);
  const [studentErr,  setStudentErr]  = useState('');
  const [modal,       setModal]       = useState(false);
  const [csvModal,    setCsvModal]    = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [form,        setForm]        = useState(emptyForm);
  const [busy,        setBusy]        = useState(false);
  const [err,         setErr]         = useState('');
  const [loading,     setLoading]     = useState(true);

  // Detail view
  const [selectedTaskId, setSelectedTaskId] = useState(null);

  // Add a staff member to the directory without leaving the ticket form, then select them.
  const saveNewStaff = async () => {
    if (!form.campus_id)        { setStaffErr('Select a campus first'); return; }
    if (!newStaff.name.trim())  { setStaffErr('Name is required'); return; }
    setStaffBusy(true); setStaffErr('');
    try {
      const r = await api.post('/master/informedby', {
        campus_id: parseInt(form.campus_id), name: newStaff.name.trim(), email: newStaff.email.trim() || null,
      });
      const added = r.data;
      const list = await api.get(`/master/informedby/${form.campus_id}`);
      setInformedBy(list.data.filter(x => x.active));
      setInfSearch(added.name);
      setForm(f => ({ ...f, informed_by_id: String(added.id), informed_by_name: added.name }));
      setNewStaff(null);
    } catch (e) { setStaffErr(e.response?.data?.error || 'Could not add staff'); }
    finally { setStaffBusy(false); }
  };

  // Add a room to the campus without leaving the ticket form, then select it.
  const saveNewRoom = async () => {
    if (!form.campus_id)       { setRoomErr('Select a campus first'); return; }
    if (!newRoom.name.trim())  { setRoomErr('Room name is required'); return; }
    setRoomBusy(true); setRoomErr('');
    try {
      const r = await api.post('/master/rooms', {
        campus_id: parseInt(form.campus_id), name: newRoom.name.trim(), code: newRoom.code.trim() || null,
      });
      const added = r.data;
      const list = await api.get(`/master/rooms/${form.campus_id}`);
      setRooms(list.data.filter(x => x.active));
      setForm(f => ({ ...f, room_id: String(added.id), asset_id:'' }));
      setNewRoom(null);
    } catch (e) { setRoomErr(e.response?.data?.error || 'Could not add room'); }
    finally { setRoomBusy(false); }
  };

  // Add a student (name + ID) without leaving the ticket form, then select them.
  const saveNewStudent = async () => {
    if (!form.campus_id)          { setStudentErr('Select a campus first'); return; }
    if (!newStudent.name.trim())  { setStudentErr('Student name is required'); return; }
    setStudentBusy(true); setStudentErr('');
    try {
      const r = await api.post('/master/students', {
        campus_id: parseInt(form.campus_id), name: newStudent.name.trim(),
        id_number: newStudent.id_number.trim().toUpperCase() || null,
      });
      const added = r.data;
      const list = await api.get(`/master/students/${form.campus_id}`);
      setStudents(list.data.filter(x => x.active));
      setInfSearch(added.name);
      setForm(f => ({ ...f, student_id: String(added.id), informed_by_name: added.name }));
      setNewStudent(null);
    } catch (e) { setStudentErr(e.response?.data?.error || 'Could not add student'); }
    finally { setStudentBusy(false); }
  };

  // Admins may remove any ticket (e.g. a mis-imported row); the API enforces this too.
  const canDelete = isAdmin;
  const delTask = async t => {
    if (!window.confirm(`Permanently delete ticket ${t.task_id}?\n\n"${t.title}"\n\nThis cannot be undone.`)) return;
    try { await api.delete(`/tasks/${t.id}`); loadTasks(); }
    catch (e) { window.alert(e.response?.data?.error || 'Delete failed'); }
  };

  // Apply a filter handed off from the Dashboard click-through (once, on mount)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('hqTaskFilter');
      if (raw) { sessionStorage.removeItem('hqTaskFilter');
        const f = JSON.parse(raw); setTab('staff');
        setFilters(prev => ({ ...prev, status:f.status||'', priority:f.priority||'', open:f.open||'' }));
      }
    } catch (e) {}
  }, []);

  // The query behind the current tab + filter row. Shared by the list and the
  // report download so what you export is exactly what you are looking at.
  const taskParams = useCallback(() => {
    const params = {};
    if (tab === 'meeting') params.discuss = 'true';   // all reporter types, flagged for meeting
    // One Tasks list. Staff, student and IT work used to live on three tabs;
    // they are all tasks, and splitting them meant checking three places to see
    // what was outstanding. The distinction survives as a filter.
    else if (filters.kind === 'it') params.category_type = 'it';
    else if (filters.kind === 'staff' || filters.kind === 'student') {
      params.reporter_type = filters.kind;
      params.exclude_it = 'true';
    }
    if (filters.search)    params.search    = filters.search;
    if (filters.status)    params.status    = filters.status;
    if (filters.priority)  params.priority  = filters.priority;
    if (filters.open)      params.open      = 'true';
    if (filters.assigned_to) params.assigned_to = filters.assigned_to;
    if (filters.campus_id) params.campus_id = filters.campus_id;
    if (tab !== 'meeting' && filters.discuss) params.discuss = 'true';
    return params;
  }, [filters, tab]);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    if (tab === 'projects') { setTasks([]); setLoading(false); return; }
    try {
      const params = taskParams();
      const r = await api.get('/tasks', { params });
      setTasks(r.data);
    } catch(e) {}
    setLoading(false);
  }, [taskParams, tab]);

  useEffect(() => { loadTasks(); }, [loadTasks]);
  useEffect(() => {
    api.get('/master/campuses').then(r => setCampuses(r.data));
    api.get('/master/categories', { params:{ type:'staff' } }).then(r => setStaffCats(r.data));
    api.get('/master/categories', { params:{ type:'student' } }).then(r => setStudentCats(r.data));
    api.get('/master/categories', { params:{ type:'it' } }).then(r => setItCats(r.data)).catch(()=>{});
    api.get('/tasks/assignees').then(r => setAssignees(r.data)).catch(()=>{});
    api.get('/tasks/it-users').then(r => setItUsers(r.data)).catch(()=>{});
  }, []);

  useEffect(() => {
    if (form.campus_id) {
      api.get(`/master/rooms/${form.campus_id}`).then(r => setRooms(r.data.filter(x => x.active)));
      api.get(`/master/informedby/${form.campus_id}`).then(r => setInformedBy(r.data.filter(x => x.active)));
      api.get(`/master/students/${form.campus_id}`).then(r => setStudents(r.data.filter(x => x.active)));
      api.get('/assets', { params: { campus_id: form.campus_id } }).then(r => setCampusAssets(r.data));
    } else {
      setRooms([]); setInformedBy([]); setStudents([]); setCampusAssets([]);
    }
  }, [form.campus_id]);

  // On the IT tab the picker offers IT's own categories; elsewhere those stay
  // hidden so they don't clutter ordinary tickets. "IT Training" is excluded —
  // training is scheduled with the "+ Schedule Training" button, which also
  // captures the lesson and trainees. The category still exists for the task
  // rows that scheduler creates.
  // Which categories to offer follows the kind chosen IN THE FORM. It used to
  // follow the tab, which no longer exists.
  const categories = form.task_kind === 'it' ? itCats.filter(c => c.name !== 'IT Training')
    : form.reporter_type === 'student' ? studentCats : staffCats;

  // Load the selected student's laptop(s)
  useEffect(() => {
    if (form.reporter_type === 'student' && form.student_id) {
      api.get('/student-laptops', { params:{ student_id: form.student_id } }).then(r => setStudentLaptops(r.data));
    } else { setStudentLaptops([]); }
  }, [form.student_id, form.reporter_type]);

  // If viewing task detail
  if (selectedTaskId) {
    return <TaskDetail taskId={selectedTaskId} onBack={() => setSelectedTaskId(null)} onSaved={loadTasks} />;
  }

  const openNew = () => {
    const defaultCampus = isAdmin ? '' : String(user.campus_ids?.[0] || '');
    // The kind now comes from the filter if one is set — raising a task while
    // looking at IT work almost always means raising IT work — and is
    // changeable in the form either way.
    const kind = ['staff', 'student', 'it'].includes(filters.kind) ? filters.kind : 'staff';
    const itTask = kind === 'it' ? itCats.find(c => c.name === 'IT Task') : null;
    setForm({ ...emptyForm, campus_id: defaultCampus,
      task_kind: kind,
      reporter_type: kind === 'student' ? 'student' : 'staff',
      category_id: itTask ? String(itTask.id) : '' });
    setItAssignees([]);
    setInfSearch(''); setErr(''); setModal(true);
  };

  const isStudent = form.reporter_type === 'student';
  const selectedCatName = categories.find(c => String(c.id) === String(form.category_id))?.name || '';
  const isLaptopCat = isStudent && ['Laptop','Charger'].includes(selectedCatName);
  // Onboarding tasks are IT's own provisioning work — nobody "reported" them and
  // they aren't tied to a room. The person's details are captured on completion.
  const isOnboarding = !!ONBOARDING[selectedCatName];
  // An "IT Task" is IT's own work — often org-wide rather than campus-specific,
  // and it can be handed to several IT people at once.
  const isITTask = selectedCatName === 'IT Task';
  // Who may hand one task to several people — mirrors the API's own rule.
  const canGroupAssign = isAdmin || user?.role === 'it_head';
  const isITMulti = isITTask && canGroupAssign;

  // Auto-fill room when asset selected
  const handleAssetChange = val => {
    setForm(f => ({ ...f, asset_id: val }));
    if (val) {
      const asset = campusAssets.find(a => String(a.id) === val);
      if (asset?.room_id) setForm(f => ({ ...f, asset_id: val, room_id: String(asset.room_id) }));
    }
  };

  const save = async () => {
    // IT Tasks: campus optional, no "reported by", one row per selected person.
    if (isITTask && canGroupAssign) {
      if (!form.title.trim())     { setErr('Task title is required'); return; }
      if (!itAssignees.length)    { setErr('Select at least one IT person'); return; }
      setBusy(true); setErr('');
      try {
        const r = await api.post('/tasks/group', {
          title: form.title.trim(),
          description: form.description || null,
          category_id: form.category_id ? parseInt(form.category_id) : null,
          priority: form.priority || 'Normal',
          due_date: form.due_date || null,
          campus_id: form.campus_id ? parseInt(form.campus_id) : null,   // blank = all campuses
          assignee_ids: itAssignees,
        });
        setModal(false); setItAssignees([]); loadTasks();
        if (r.data?.count > 1) alert(`Created ${r.data.count} tasks — one for each of: ${r.data.assignees.join(', ')}`);
      } catch(e) { setErr(e.response?.data?.error || 'Save failed'); }
      finally { setBusy(false); }
      return;
    }
    if (!form.campus_id)               { setErr('Campus is required'); return; }
    if (!isOnboarding && !(infSearch || form.informed_by_name).trim()) { setErr(`Reported By (${isStudent ? 'Student' : 'Staff'}) is required`); return; }
    if (!isOnboarding && !form.title.trim()) { setErr('Task title is required'); return; }
    setBusy(true); setErr('');
    try {
      const payload = {
        ...form,
        campus_id:   parseInt(form.campus_id),
        room_id:     form.room_id     ? parseInt(form.room_id)     : null,
        category_id: form.category_id ? parseInt(form.category_id) : null,
        asset_id:    form.asset_id    ? parseInt(form.asset_id)    : null,
        reporter_type: form.reporter_type,
        student_id:       isStudent ? (form.student_id ? parseInt(form.student_id) : null) : null,
        student_laptop_id: (isStudent && isLaptopCat && form.student_laptop_id) ? parseInt(form.student_laptop_id) : null,
        informed_by_id:   isStudent ? null : (form.informed_by_id ? parseInt(form.informed_by_id) : null),
        informed_by_name: infSearch || form.informed_by_name,
        // Admin's pick; for IT staff the server assigns the ticket to themselves.
        assigned_to: form.assigned_to ? parseInt(form.assigned_to) : null,
      };
      await api.post('/tasks', payload);
      setModal(false); loadTasks();
    } catch(e) { setErr(e.response?.data?.error || 'Save failed'); }
    finally { setBusy(false); }
  };

  const canEdit = task => isAdmin || can('tasks','edit');
  const age = task => Math.floor((Date.now() - new Date(task.created_at)) / 86400000);
  const filteredInformed = informedBy.filter(p => !infSearch || p.name.toLowerCase().includes(infSearch.toLowerCase()));
  const filteredStudents = students.filter(s => !infSearch ||
    s.name.toLowerCase().includes(infSearch.toLowerCase()) ||
    (s.id_number||'').toLowerCase().includes(infSearch.toLowerCase()));

  // Filtered asset lists for task form
  const staffAssets = form.informed_by_id
    ? campusAssets.filter(a => String(a.staff_id) === form.informed_by_id)
    : [];
  const roomAssets = form.room_id
    ? campusAssets.filter(a => String(a.room_id) === form.room_id)
    : [];
  // Combined unique asset options: room assets first, then staff assets not already in room list
  const assetOptions = [
    ...roomAssets,
    ...staffAssets.filter(a => !roomAssets.find(r => r.id === a.id))
  ];
  const selectedAsset = campusAssets.find(a => String(a.id) === form.asset_id);

  // Download a tasks report grouped person-wise and campus-wise (.xlsx).
  const downloadReport = async () => {
    // Export exactly what the current tab + filters show, not the whole table.
    let rows = [];
    try { rows = (await api.get('/tasks', { params: taskParams() })).data; }
    catch (e) { return; }
    if (!rows.length) { alert('Nothing to export — the current filters return no tasks.'); return; }
    const d = v => v ? new Date(v).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'2-digit' }) : '';
    const isOpen = t => !['Completed','Closed'].includes(t.status);
    const line = t => ({
      'Task ID': t.task_id, 'Title': t.title, 'Type': t.reporter_type || 'staff',
      'Campus': t.campus_name || '—', 'Assignee': t.assigned_to_name || 'Unassigned',
      'Status': t.status, 'Priority': t.priority, 'Created': d(t.created_at), 'Completed': d(t.completed_at),
    });
    const wb = XLSX.utils.book_new();
    // By Person — sorted by assignee then campus
    const byPerson = [...rows].sort((a,b) =>
      (a.assigned_to_name||'~').localeCompare(b.assigned_to_name||'~') || (a.campus_name||'').localeCompare(b.campus_name||''));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(byPerson.map(line)), 'By Person');
    // By Campus — sorted by campus then assignee
    const byCampus = [...rows].sort((a,b) =>
      (a.campus_name||'~').localeCompare(b.campus_name||'~') || (a.assigned_to_name||'').localeCompare(b.assigned_to_name||''));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(byCampus.map(line)), 'By Campus');
    // Summary counts
    const tally = (keyFn) => {
      const m = {};
      rows.forEach(t => { const k = keyFn(t) || '—'; (m[k] = m[k] || { Total:0, Open:0 }); m[k].Total++; if (isOpen(t)) m[k].Open++; });
      return Object.entries(m).sort((a,b)=>b[1].Open-a[1].Open).map(([k,v]) => ({ Name:k, Open:v.Open, Total:v.Total }));
    };
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(tally(t=>t.assigned_to_name||'Unassigned')), 'Summary — Person');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(tally(t=>t.campus_name)), 'Summary — Campus');
    // Record what was filtered, so a saved file can be trusted later.
    const applied = Object.entries({
      Tab: filters.kind === 'it' ? 'IT Tasks' : filters.kind === 'student' ? 'Student Tasks' : filters.kind === 'staff' ? 'Staff Tasks' : 'All Tasks',
      Campus: campuses.find(c => String(c.id) === String(filters.campus_id))?.name,
      Status: filters.status, Priority: filters.priority,
      'Open only': filters.open ? 'Yes' : '',
      Person: assignees.find(a => String(a.id) === String(filters.assigned_to))?.full_name
              || (filters.assigned_to === 'none' ? 'Unassigned' : ''),
      Search: filters.search,
      'For meeting': filters.discuss ? 'Yes' : '',
    }).filter(([, v]) => v).map(([k, v]) => ({ Filter: k, Value: v }));
    applied.push({ Filter: 'Rows exported', Value: String(rows.length) });
    applied.push({ Filter: 'Generated', Value: new Date().toLocaleString('en-IN') });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(applied), 'Filters applied');

    const tag = filters.kind || 'all';
    XLSX.writeFile(wb, `tasks-report-${tag}-${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'space-between', marginBottom:16 }}>
        <h2 style={{ fontSize:18, fontWeight:600 }}>Task Management</h2>
        <div className="flex-gap" style={{ gap:8 }}>
          {/* These tested for the old staff/student/it tabs. Collapsing the
              tabs left both buttons permanently hidden. */}
          {['admin','it_head'].includes(user?.role) && tab === 'tasks' && <button className="btn btn-secondary" onClick={() => setReassignOpen(true)}>⇄ Reassign</button>}
          {tab === 'tasks' && <button className="btn btn-secondary" onClick={downloadReport}>⬇ Report</button>}
          {can('tasks','create') && tab === 'tasks' && <button className="btn btn-secondary" onClick={() => setCsvModal(true)}>⬆ Bulk Upload</button>}
          {can('tasks','create') && tab === 'tasks' && <button className="btn btn-primary" onClick={openNew}>+ Add Task</button>}
        </div>
      </div>
      {reassignOpen && <ReassignModal itUsers={itUsers} onClose={() => setReassignOpen(false)} onDone={() => { setReassignOpen(false); loadTasks(); }} />}

      {/* Tabs: Tasks · Health Check · Routines. Staff, student and IT work are
          one list now — the kind is a filter in the row below, not a tab. */}
      <div style={{ display:'flex', gap:0, marginBottom:16, borderBottom:'1px solid var(--border)' }}>
        {[['tasks','✓ Tasks'],['healthcheck','🩺 Health Check'],['routines','🔁 Routines']].map(([id,label]) => (
          <button key={id} onClick={() => setTab(id)}
            style={{
              padding:'9px 22px', fontSize:13, fontFamily:'var(--font)', cursor:'pointer',
              background:'transparent', border:'none',
              borderBottom: tab === id ? '2px solid var(--accent)' : '2px solid transparent',
              color: tab === id ? 'var(--accent)' : 'var(--text2)',
              fontWeight: tab === id ? 600 : 400, marginBottom:-1, transition:'all 0.15s'
            }}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'healthcheck' ? <HealthChecks /> : tab === 'routines' ? <Routines /> : (
       <>
      {/* Filters */}
      <div className="card" style={{ marginBottom:16, padding:12 }}>
        <div style={{ display:'grid', gridTemplateColumns:'2fr repeat(auto-fit, minmax(140px, 1fr))', gap:10 }}>
          <input placeholder="Search tasks…" value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} />
          <select value={filters.kind} onChange={e => setFilters(f => ({ ...f, kind: e.target.value }))}>
            <option value="">All tasks</option>
            <option value="staff">Staff</option>
            <option value="student">Student</option>
            <option value="it">IT</option>
          </select>
          <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
            <option value="">All Status</option>
            <option>Pending</option><option>Assigned</option><option>In Progress</option><option>Needs Discussion</option><option>Discussed</option><option>Completed</option><option>Closed</option>
          </select>
          <select value={filters.priority} onChange={e => setFilters(f => ({ ...f, priority: e.target.value }))}>
            <option value="">All Priority</option>
            <option>High</option><option>Normal</option><option>Low</option>
          </select>
          <select value={filters.assigned_to} onChange={e => setFilters(f => ({ ...f, assigned_to: e.target.value }))} title="Filter by the IT person working on it">
            <option value="">By Person — All</option>
            <option value="none">— Unassigned —</option>
            {assignees.map(a => <option key={a.id} value={a.id}>{a.full_name}</option>)}
          </select>
          {isAdmin && (
            <select value={filters.campus_id} onChange={e => setFilters(f => ({ ...f, campus_id: e.target.value }))}>
              <option value="">All Campuses</option>
              {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <select value={filters.discuss} onChange={e => setFilters(f => ({ ...f, discuss: e.target.value }))}>
            <option value="">All</option><option value="true">Meeting Discussion</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {loading ? <div style={{ padding:40, textAlign:'center', color:'var(--text2)' }}>Loading…</div>
        : !tasks.length ? <div className="empty-state"><h3>No tasks found</h3><p>Add your first task or adjust filters.</p></div>
        : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Task ID</th><th>Title</th><th>Campus · Room</th>
                  <th>Asset</th><th>Priority</th><th>Status</th>
                  {/* IT tasks have no reporter — nobody raised them, IT planned
                      them — so that column names the person who created it. */}
                  {/* One list holds all three kinds, so the column cannot be
                      named after the tab any more. */}
                  <th>Reported by / Raised by</th><th>Working On</th><th>Created</th>
                  {canDelete && <th></th>}
                </tr>
              </thead>
              <tbody>
                {tasks.map(t => (
                  <tr key={t.id} style={{ cursor:'pointer' }} onClick={() => setSelectedTaskId(t.id)}>
                    <td><span className="mono" style={{ fontSize:12, color:'var(--accent)' }}>{t.task_id}</span></td>
                    <td>
                      <div style={{ fontWeight:500 }}>{t.title}</div>
                      {t.source === 'incident' && <span className="badge badge-faulty" style={{ marginTop:3, fontSize:10 }}>🚨 Incident</span>}
                      {t.source === 'email' && <span className="badge badge-normal" style={{ marginTop:3, fontSize:10 }}>✉ Email</span>}
                      {t.source === 'erp' && <span className="badge badge-normal" style={{ marginTop:3, fontSize:10 }}>🧾 ERP</span>}
                      {['project','meeting'].includes(t.source) && t.container_title && (
                        <span className="badge badge-repair" title="Open its project / meeting"
                          style={{ marginTop:3, fontSize:10, cursor:'pointer' }}
                          onClick={e => { e.stopPropagation();
                            const dest = t.container_type === 'project' ? 'projects'
                              : 'meetings';
                            go && go(dest, t.container_id); }}>
                          <b>{t.container_type === 'meeting' ? '👥 MEETING' : '📁 PROJECT'}</b> · {t.container_title}{t.phase_name ? ` · ${t.phase_name}` : ''} ↗
                        </span>
                      )}
                      {t.discuss_in_meeting && <span className="badge badge-discuss" style={{ marginTop:3, fontSize:10 }}>🗣 Meeting</span>}
                      {/* Assigned to several IT people — show how the batch is going */}
                      {t.group_progress && t.group_progress.total > 1 && (
                        <span className="badge" style={{ marginTop:3, marginLeft:4, fontSize:10, fontWeight:700,
                          background: t.group_progress.done === t.group_progress.total ? 'rgba(45,168,102,0.15)' : 'var(--bg3)',
                          color: t.group_progress.done === t.group_progress.total ? 'var(--green)' : 'var(--text2)' }}
                          title="This task was assigned to several IT persons; each has their own copy">
                          👥 {t.group_progress.done}/{t.group_progress.total} done
                        </span>
                      )}
                    </td>
                    <td>
                      <div style={{ fontSize:12 }}>{t.campus_name || <span style={{ color:'var(--text2)' }}>All campuses</span>}</div>
                      {t.room_name && <div style={{ fontSize:11, color:'var(--text2)' }}>📍 {t.room_name}</div>}
                    </td>
                    <td>
                      {t.asset_tag
                        ? <div><span className="mono" style={{ fontSize:11, color:'var(--accent)' }}>{t.asset_tag}</span><div style={{ fontSize:11, color:'var(--text2)' }}>{t.asset_type || t.asset_brand}</div></div>
                        : <span style={{ color:'var(--text2)', fontSize:12 }}>—</span>}
                    </td>
                    <td>{priorityBadge(t.priority)}</td>
                    <td>{statusBadge(t.status)}</td>
                    <td>
                      <div style={{ fontSize:12, color:'var(--text2)' }}>
                        {t.student_name || t.informed_by_display || t.informed_by_name
                          || t.created_by_name || '—'}
                      </div>
                      {t.reporter_type === 'student' && (t.student_grade || t.student_section) && (
                        <div style={{ fontSize:11, color:'var(--text3)' }}>Grade {t.student_grade}{t.student_section ? `-${t.student_section}` : ''}{t.student_id_number ? ` · ${t.student_id_number}` : ''}</div>
                      )}
                    </td>
                    <td>
                      {t.assigned_to_name
                        ? <span style={{ fontSize:12 }}>👤 {t.assigned_to_name}</span>
                        : <span style={{ fontSize:12, color:'var(--text3)' }}>Unassigned</span>}
                    </td>
                    <td><span style={{ fontSize:12, color:'var(--text2)' }}>{new Date(t.created_at).toLocaleDateString()}</span></td>
                    {canDelete && (
                      <td onClick={e => e.stopPropagation()}>
                        <button className="btn btn-danger btn-sm" onClick={() => delTask(t)}>Delete</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
       </>
      )}

      {/* Add Task Modal */}
      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && false && setModal(false)}>
          <div className="modal" style={{ maxWidth:640 }}>
            <div className="modal-header">
              <span className="modal-title">Add Task</span>
              <button className="modal-close" onClick={() => setModal(false)}>×</button>
            </div>

            {/* Step 0 — what kind of task. This used to be decided by whichever
                tab you were on; with one list it has to be asked, and it is
                what drives the categories and the fields below. */}
            <div className="form-group">
              <label>Task for *</label>
              <div className="flex-gap" style={{ gap:8, flexWrap:'wrap' }}>
                {[['staff','Staff'],['student','Student'],['it','IT team']].map(([k,label]) => (
                  <button key={k} type="button"
                    onClick={() => setForm(f => ({ ...f, task_kind:k,
                      reporter_type: k === 'student' ? 'student' : 'staff',
                      category_id: k === 'it'
                        ? String(itCats.find(c => c.name === 'IT Task')?.id || '') : '',
                      informed_by_id:'', informed_by_name:'', student_id:'', student_laptop_id:'' }))}
                    className={`btn btn-sm ${form.task_kind === k ? 'btn-primary' : 'btn-secondary'}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Step 1 — Campus */}
            <div className="form-group">
              <label>Campus {isITMulti
                ? <span style={{ fontSize:11, color:'var(--text2)', fontWeight:400 }}>— optional; leave blank and each person’s task sits at their own campus</span>
                : '*'}</label>
              <select value={form.campus_id} disabled={!isAdmin && user?.campus_ids?.length === 1}
                onChange={e => setForm(f => ({ ...f, campus_id: e.target.value, room_id:'', asset_id:'', informed_by_id:'', informed_by_name:'', student_id:'' }))}>
                <option value="">{isITMulti ? '— Each person’s own campus —' : '— Select campus —'}</option>
                {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            {/* An IT Task is IT's own work: nobody "reported" it and it isn't
                tied to a room or asset, so those steps are skipped. */}
            {form.campus_id && !(isITMulti) && (<>
              {/* Step 2 — Reported By (staff OR student depending on tab).
                  Skipped for onboarding tasks — details come at completion. */}
              {isOnboarding ? (
                <div style={{ fontSize:12, color:'var(--text2)', background:'var(--bg3)', border:'1px solid var(--border)',
                  borderRadius:'var(--radius)', padding:'8px 10px', marginBottom:12 }}>
                  ℹ️ No “Reported By” or room needed — you'll enter the
                  {selectedCatName === 'New Staff' ? ' staff member’s' : ' student’s'} details when you complete this task,
                  and they'll be added to Master Data automatically.
                </div>
              ) : isStudent ? (
                <div className="form-group" style={{ position:'relative' }}>
                  <label>Reported By (Student) * <span style={{ fontSize:11, color:'var(--text2)' }}>— search name or ID</span></label>
                  <input value={infSearch}
                    onChange={e => { setInfSearch(e.target.value); setForm(f => ({ ...f, student_id:'', informed_by_name: e.target.value })); }}
                    placeholder="Type student name or ID number…" autoComplete="off" />
                  {infSearch && !form.student_id && filteredStudents.length > 0 && (
                    <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:99, background:'var(--bg2)', border:'1px solid var(--accent)', borderRadius:'0 0 var(--radius) var(--radius)', maxHeight:180, overflowY:'auto', boxShadow:'0 8px 24px rgba(0,0,0,0.4)' }}>
                      {filteredStudents.slice(0,30).map(s => (
                        <div key={s.id} onClick={() => { setInfSearch(s.name); setForm(f => ({ ...f, student_id: String(s.id), informed_by_name: s.name })); }}
                          style={{ padding:'8px 12px', cursor:'pointer', fontSize:13, borderBottom:'1px solid var(--border)' }}
                          onMouseEnter={e => e.currentTarget.style.background='var(--bg3)'}
                          onMouseLeave={e => e.currentTarget.style.background=''}>
                          <div style={{ fontWeight:500 }}>{s.name}</div>
                          <div style={{ fontSize:11, color:'var(--text2)' }}>
                            {s.id_number ? `ID: ${s.id_number}` : ''}{s.grade ? ` · Grade ${s.grade}` : ''}{s.section ? `-${s.section}` : ''}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Quick-add a student who isn't in the list yet (name + ID only) */}
                  {!newStudent ? (
                    <button className="btn btn-secondary btn-sm" style={{ marginTop:6, fontSize:11 }}
                      onClick={() => setNewStudent({ name: infSearch || '', id_number:'' })}>+ Add student</button>
                  ) : (
                    <div style={{ marginTop:8, padding:10, background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:'var(--radius)' }}>
                      <div style={{ fontSize:12, fontWeight:600, marginBottom:6 }}>New student {form.campus_id ? '' : '— select a campus first'}</div>
                      <div className="form-row" style={{ gap:8 }}>
                        <input value={newStudent.name} placeholder="Student name *"
                          onChange={e => setNewStudent(s => ({ ...s, name: e.target.value }))} />
                        {/* ID is forced to caps as it's typed — e.g. ib-12-123 → IB-12-123 */}
                        <input value={newStudent.id_number} placeholder="ID Number  e.g. IB-12-123"
                          style={{ textTransform:'uppercase' }}
                          onChange={e => setNewStudent(s => ({ ...s, id_number: e.target.value.toUpperCase() }))} />
                      </div>
                      {studentErr && <div className="err-msg" style={{ marginTop:6 }}>{studentErr}</div>}
                      <div className="flex-gap" style={{ gap:6, marginTop:8 }}>
                        <button className="btn btn-primary btn-sm" disabled={studentBusy} onClick={saveNewStudent}>
                          {studentBusy ? 'Adding…' : 'Add & select'}
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={() => { setNewStudent(null); setStudentErr(''); }}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="form-group" style={{ position:'relative' }}>
                  <label>Reported By (Staff) * <span style={{ fontSize:11, color:'var(--text2)' }}>— who called IT?</span></label>
                  <input value={infSearch}
                    onChange={e => { setInfSearch(e.target.value); setInfOpen(true); setForm(f => ({ ...f, informed_by_id:'', informed_by_name: e.target.value, asset_id:'' })); }}
                    onFocus={() => setInfOpen(true)}
                    onBlur={() => setTimeout(() => setInfOpen(false), 120)}
                    placeholder="Type staff name to search…" autoComplete="off" />
                  {infOpen && infSearch && filteredInformed.length > 0 && (
                    <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:99, background:'var(--bg2)', border:'1px solid var(--accent)', borderRadius:'0 0 var(--radius) var(--radius)', maxHeight:160, overflowY:'auto', boxShadow:'0 8px 24px rgba(0,0,0,0.4)' }}>
                      {filteredInformed.map(p => (
                        // onMouseDown fires before the input's blur, so the pick always registers
                        <div key={p.id} onMouseDown={() => { setInfSearch(p.name); setInfOpen(false); setForm(f => ({ ...f, informed_by_id: String(p.id), informed_by_name: p.name, asset_id:'' })); }}
                          style={{ padding:'8px 12px', cursor:'pointer', fontSize:13, borderBottom:'1px solid var(--border)' }}
                          onMouseEnter={e => e.currentTarget.style.background='var(--bg3)'}
                          onMouseLeave={e => e.currentTarget.style.background=''}>
                          <div style={{ fontWeight:500 }}>{p.name}</div>
                          {p.designation && <div style={{ fontSize:11, color:'var(--text2)' }}>{p.designation}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Quick-add a staff member who isn't in the directory yet (name + email only) */}
                  {!newStaff ? (
                    <button className="btn btn-secondary btn-sm" style={{ marginTop:6, fontSize:11 }}
                      onClick={() => setNewStaff({ name: infSearch || '', email:'' })}
                      title="Add a staff member who is not in the list">+ Add staff</button>
                  ) : (
                    <div style={{ marginTop:8, padding:10, background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:'var(--radius)' }}>
                      <div style={{ fontSize:12, fontWeight:600, marginBottom:6 }}>New staff {form.campus_id ? '' : '— select a campus first'}</div>
                      <div className="form-row" style={{ gap:8 }}>
                        <input value={newStaff.name} placeholder="Full name *"
                          onChange={e => setNewStaff(s => ({ ...s, name: e.target.value }))} />
                        <input value={newStaff.email} placeholder="Email" type="email"
                          onChange={e => setNewStaff(s => ({ ...s, email: e.target.value }))} />
                      </div>
                      {staffErr && <div className="err-msg" style={{ marginTop:6 }}>{staffErr}</div>}
                      <div className="flex-gap" style={{ gap:6, marginTop:8 }}>
                        <button className="btn btn-primary btn-sm" disabled={staffBusy} onClick={saveNewStaff}>
                          {staffBusy ? 'Adding…' : 'Add & select'}
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={() => { setNewStaff(null); setStaffErr(''); }}>Cancel</button>
                      </div>
                      <div style={{ fontSize:11, color:'var(--text2)', marginTop:6 }}>
                        No login is created. You can give them portal access later from <b>Users → Create Login</b>.
                      </div>
                    </div>
                  )}

                  {/* Show staff's personal assets if any */}
                  {staffAssets.length > 0 && !form.asset_id && (
                    <div style={{ marginTop:6, fontSize:12, color:'var(--text2)' }}>
                      Assets linked to this staff:&nbsp;
                      {staffAssets.map(a => (
                        <button key={a.id} className="btn btn-secondary btn-sm" style={{ marginRight:4, marginTop:4, fontSize:11 }}
                          onClick={() => handleAssetChange(String(a.id))}>
                          {a.asset_tag} · {a.asset_type || a.brand}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Step 3 — Room (staff only; students have no room) */}
              {!isStudent && !isOnboarding && (
              <div className="form-group">
                <label>Room <span style={{ fontSize:11, color:'var(--text2)' }}>— where is the issue?</span></label>
                <select value={form.room_id}
                  onChange={e => setForm(f => ({ ...f, room_id: e.target.value, asset_id:'' }))}>
                  <option value="">— Select room —</option>
                  {rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>

                {/* Quick-add a room that isn't in the list yet */}
                {!newRoom ? (
                  <button className="btn btn-secondary btn-sm" style={{ marginTop:6, fontSize:11 }}
                    onClick={() => setNewRoom({ name:'', code:'' })}>+ Add room</button>
                ) : (
                  <div style={{ marginTop:8, padding:10, background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:'var(--radius)' }}>
                    <div style={{ fontSize:12, fontWeight:600, marginBottom:6 }}>New room {form.campus_id ? '' : '— select a campus first'}</div>
                    <div className="form-row" style={{ gap:8 }}>
                      <input value={newRoom.name} placeholder="Room name *  e.g. Grade 6A"
                        onChange={e => setNewRoom(s => ({ ...s, name: e.target.value }))} />
                      <input value={newRoom.code} placeholder="Code (optional) e.g. LAB3"
                        onChange={e => setNewRoom(s => ({ ...s, code: e.target.value.toUpperCase() }))} />
                    </div>
                    {roomErr && <div className="err-msg" style={{ marginTop:6 }}>{roomErr}</div>}
                    <div className="flex-gap" style={{ gap:6, marginTop:8 }}>
                      <button className="btn btn-primary btn-sm" disabled={roomBusy} onClick={saveNewRoom}>
                        {roomBusy ? 'Adding…' : 'Add & select'}
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => { setNewRoom(null); setRoomErr(''); }}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
              )}

              {/* Step 4 — Affected Asset (staff only; not for onboarding) */}
              {!isStudent && !isOnboarding && (
              <div className="form-group">
                <label>
                  Affected Asset
                  <span style={{ fontSize:11, color:'var(--text2)' }}>
                    {' '}— optional
                    {form.room_id && roomAssets.length > 0 && ` · ${roomAssets.length} asset${roomAssets.length>1?'s':''} in this room`}
                    {form.informed_by_id && staffAssets.length > 0 && ` · ${staffAssets.length} asset${staffAssets.length>1?'s':''} linked to staff`}
                    {!form.room_id && !form.informed_by_id && ' · select room or staff to filter'}
                  </span>
                </label>
                <select value={form.asset_id} onChange={e => handleAssetChange(e.target.value)}>
                  <option value="">— No specific asset —</option>
                  {/* Room assets group */}
                  {roomAssets.length > 0 && (
                    <optgroup label={`Assets in room${rooms.find(r=>String(r.id)===form.room_id)?.name ? ': '+rooms.find(r=>String(r.id)===form.room_id).name : ''}`}>
                      {roomAssets.map(a => (
                        <option key={a.id} value={a.id}>{a.asset_tag} · {a.asset_type || a.brand} {a.model ? `(${a.model})` : ''}</option>
                      ))}
                    </optgroup>
                  )}
                  {/* Staff assets group */}
                  {staffAssets.filter(a => !roomAssets.find(r=>r.id===a.id)).length > 0 && (
                    <optgroup label={`Assets assigned to ${infSearch}`}>
                      {staffAssets.filter(a => !roomAssets.find(r=>r.id===a.id)).map(a => (
                        <option key={a.id} value={a.id}>{a.asset_tag} · {a.asset_type || a.brand} {a.model ? `(${a.model})` : ''}</option>
                      ))}
                    </optgroup>
                  )}
                  {/* If no room/staff filter, show all campus assets */}
                  {!form.room_id && !form.informed_by_id && campusAssets.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.asset_tag} · {a.asset_type || a.brand}{a.room_name ? ` (${a.room_name})` : a.staff_name ? ` → ${a.staff_name}` : ''}
                    </option>
                  ))}
                </select>
                {selectedAsset && <AssetMiniCard asset={selectedAsset} />}
              </div>
              )}
            </>)}

            {/* Task Info — onboarding tasks are titled from the category, then
                renamed to "New Staff — <name>" once you complete them. */}
            {!isOnboarding && (
              <div className="form-group">
                <label>Task Title *</label>
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. Mouse not working in A102" />
              </div>
            )}

            {/* Due date is for IT's own planned work; staff/student tickets are
                driven by the SLA clock, not a date someone types. */}
            {isITMulti && (
              <div className="form-group">
                <label>Due date <span style={{ fontSize:11, color:'var(--text2)' }}>— optional; when must this be finished?</span></label>
                <input type="date" value={form.due_date || ''}
                  onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} />
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>Category</label>
                <select value={form.category_id} onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}>
                  <option value="">— Select —</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              {!isOnboarding && (
                <div className="form-group">
                  <label>Priority</label>
                  <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                    <option>Normal</option><option>High</option><option>Low</option>
                  </select>
                </div>
              )}
            </div>
            {/* The checklist isn't shown here — the steps are ticked on the ticket
                as IT actually does them, not up front. */}

            {/* Assignment: admins choose the IT person; IT staff own what they raise */}
            {isITMulti ? (
              <div className="form-group">
                {(() => {
                  // Other admin accounts aren't people who do campus IT work, but
                  // you can always assign an IT task to yourself.
                  const people = itUsers.filter(u => u.role !== 'admin' || u.id === user.id);
                  const all = people.length > 0 && people.every(u => itAssignees.includes(u.id));
                  const mine = itAssignees.includes(user.id);
                  const toggle = id => setItAssignees(a => a.includes(id) ? a.filter(x => x !== id) : [...a, id]);
                  return (<>
                    <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                      <label style={{ margin:0 }}>Assign to <span style={{ fontSize:11, color:'var(--text2)' }}>— one task is created per person</span></label>
                      <div className="flex-gap" style={{ gap:6 }}>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => toggle(user.id)}>
                          {mine ? '− Remove me' : '+ Assign to me'}
                        </button>
                        <button type="button" className="btn btn-secondary btn-sm"
                          onClick={() => setItAssignees(all ? [] : people.map(u => u.id))}>
                          {all ? 'Clear all' : 'All IT persons'}
                        </button>
                      </div>
                    </div>
                    <div style={{ border:'1px solid var(--border)', borderRadius:'var(--radius)',
                      maxHeight:170, overflowY:'auto', padding:'4px 0' }}>
                      {!people.length && <div style={{ padding:'8px 10px', fontSize:12.5, color:'var(--text2)' }}>No active IT staff found.</div>}
                      {people.map(u => (
                        <label key={u.id} className="opt" style={{ padding:'6px 10px' }}>
                          <input type="checkbox" checked={itAssignees.includes(u.id)} onChange={() => toggle(u.id)} />
                          <span>{u.full_name}</span>
                          {u.id === user.id && <span style={{ fontSize:10.5, fontWeight:700, color:'var(--accent)' }}>(you)</span>}
                          {['it_head','it_lead'].includes(u.role) && <span className="badge" style={{ fontSize:9.5, background:'var(--bg3)', color:'var(--text2)' }}>
                            {u.role === 'it_head' ? 'IT HEAD' : 'IT LEAD'}</span>}
                          {u.campus_name && <span className="opt-meta">{u.campus_name}</span>}
                        </label>
                      ))}
                    </div>
                    <div style={{ fontSize:11.5, color:'var(--text2)', marginTop:5 }}>
                      {itAssignees.length
                        ? `${itAssignees.length} selected — ${itAssignees.length} separate task${itAssignees.length > 1 ? 's' : ''} will be created, each tracked on its own.`
                        : 'Select the IT persons who must do this.'}
                    </div>
                  </>);
                })()}
              </div>
            ) : isAdmin ? (
              <div className="form-group">
                <label>Assign to <span style={{ fontSize:11, color:'var(--text2)' }}>— which IT person will handle it?</span></label>
                <select value={form.assigned_to} onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value }))}>
                  <option value="">— Unassigned (anyone can pick it up) —</option>
                  {itUsers.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                </select>
              </div>
            ) : (
              <div style={{ fontSize:12, color:'var(--text2)', background:'var(--bg3)', border:'1px solid var(--border)',
                borderRadius:'var(--radius)', padding:'8px 10px', marginBottom:12 }}>
                👤 This task will be assigned to <b>you</b> ({user.full_name}).
              </div>
            )}

            {/* Student laptop link — shown when student + Laptop/Charger category */}
            {isLaptopCat && form.student_id && (
              <div className="form-group" style={{ background:'rgba(79,142,247,0.06)', border:'1px solid rgba(79,142,247,0.2)', borderRadius:'var(--radius)', padding:12 }}>
                <label>💻 Student's Laptop {studentLaptops.length > 0 && '(auto-linked)'}</label>
                {studentLaptops.length === 0 ? (
                  <div style={{ fontSize:12, color:'var(--amber)' }}>
                    No laptop on record for this student. (Laptops are issued Grade 6 onwards — register it under Assets → Student Laptops.)
                  </div>
                ) : (
                  <>
                    <select value={form.student_laptop_id} onChange={e => setForm(f => ({ ...f, student_laptop_id: e.target.value }))}>
                      {studentLaptops.length > 1 && <option value="">— Select laptop —</option>}
                      {studentLaptops.map(l => (
                        <option key={l.id} value={l.id}>{l.brand} {l.model} · S/N {l.serial_number || '—'}</option>
                      ))}
                    </select>
                    {(() => {
                      const l = studentLaptops.find(x => String(x.id) === String(form.student_laptop_id)) || (studentLaptops.length === 1 ? studentLaptops[0] : null);
                      if (l && !form.student_laptop_id && studentLaptops.length === 1) {
                        // auto-select the single laptop
                        setTimeout(() => setForm(f => f.student_laptop_id ? f : ({ ...f, student_laptop_id: String(l.id) })), 0);
                      }
                      return l ? (
                        <div style={{ marginTop:8, fontSize:12, color:'var(--text2)' }}>
                          Serial: <span className="mono" style={{ color:'var(--text)' }}>{l.serial_number || '—'}</span>
                          {' · '}Charger: <span className="mono" style={{ color:'var(--text)' }}>{l.charger_serial || '—'}</span>
                          {' · '}OS License: {l.os_license ? 'Yes' : 'No'}
                          {l.distributed_date && <> · Distributed {new Date(l.distributed_date).toLocaleDateString()}</>}
                        </div>
                      ) : null;
                    })()}
                  </>
                )}
              </div>
            )}

            {!isOnboarding && (
              <div className="form-group">
                <label>Description</label>
                <textarea value={form.description} rows={3}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Additional details…" />
              </div>
            )}

            {err && <div className="err-msg">{err}</div>}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Create Task'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk upload of past work reports (e.g. pre-launch June tickets) */}
      {csvModal && (
        <CSVUploadModal
          title="Bulk Upload Work Report"
          sampleFile={{
            name: 'work-report-sample.csv',
            content: 'Campus,Room,Category,Title,Description,Reported By,Priority,Status,Resolution,Created Date,Completed Date\n'
              + 'CBE-MAIN,Grade 6A,Projector,Projector not turning on,No power to the projector,Rajesh Kumar,High,Completed,Replaced the power cable,2026-06-03,2026-06-03\n'
              + 'CBE-MAIN,Lab 2,Computer,PC very slow,System takes 10 min to boot,Meera R,Normal,Completed,Cleared temp files and added RAM,2026-06-11,2026-06-12\n'
              + 'CBE-KG,Staff Room,Printer,Printer paper jam,,Anitha S,Low,Completed,Cleared the jam,2026-06-18,2026-06-18\n',
          }}
          formatHint={
            <>
              <strong>Required:</strong> Campus, Title, <b>Created Date</b><br />
              <strong>Optional:</strong> Room, Category, Description, Reported By, Priority (Low/Normal/High),
              Status (Pending / Assigned / In Progress / Completed / Closed), Resolution, Completed Date<br />
              <strong>Campus</strong> = campus code or name. <strong>Created Date</strong> = the real date of the work —
              it sets the ticket's date <em>and</em> the month in its ID (e.g. a 14 June row becomes
              <span className="mono"> CBE-MAIN-2026-06-001</span>). Accepts <span className="mono">2026-06-14</span> or
              <span className="mono"> 14-06-2026</span>. Rows without a valid date are rejected (never dated today).<br />
              If a Resolution is given and Status is blank, the ticket is imported as <b>Completed</b>.
            </>
          }
          columns={[
            { key:'campus',      label:'Campus' },
            { key:'room',        label:'Room' },
            { key:'category',    label:'Category' },
            { key:'title',       label:'Title' },
            { key:'priority',    label:'Priority' },
            { key:'status',      label:'Status' },
            { key:'created_at',  label:'Created' },
          ]}
          mapRow={r => {
            // NOTE: parseCSV lower-cases headers and turns spaces into underscores,
            // so "Created Date" arrives as `created_date`.
            const pick = (...keys) => { for (const k of keys) { const v = r[k]; if (v && String(v).trim()) return String(v).trim(); } return ''; };
            const title  = pick('title','issue','problem','work','work_done','description');
            const campus = pick('campus','campus_code','campuscode','site','school');
            const created = pick('created_date','created_at','date','reported_date','complaint_date',
                                 'date_of_complaint','reported_on','work_date','logged_date','createddate');
            if (!title || !campus || !created) return null;   // campus, title & date are required
            return {
              campus, title,
              room:        pick('room','room_number','room_no','roomno'),
              category:    pick('category','type','issue_type'),
              description: pick('description','details','remarks','notes'),
              reported_by: pick('reported_by','reporter','informed_by','staff','staff_name','requested_by'),
              priority:    pick('priority'),
              status:      pick('status'),
              resolution:  pick('resolution','action_taken','action','solution','remedy'),
              created_at:  created,
              completed_at:pick('completed_date','completed_at','closed_date','resolved_date','date_closed','completion_date'),
            };
          }}
          optionLabel="Import duplicates anyway (a row already in the portal with the same campus, title and work date)"
          onUpload={(rows, allowDup) =>
            api.post('/tasks/import', { records: rows, allow_duplicates: !!allowDup }).then(r => r.data)}
          onDone={loadTasks}
          onClose={() => setCsvModal(false)}
        />
      )}
    </div>
  );
}

// Reassign one person's tasks to another (e.g. when someone leaves). Shows how
// many open tasks the person has before you commit. Admin / IT Head only.
function ReassignModal({ itUsers, onClose, onDone }) {
  const [from, setFrom] = React.useState('');
  const [to, setTo] = React.useState('');
  const [scope, setScope] = React.useState('open');   // 'open' | 'all'
  const [counts, setCounts] = React.useState(null);    // { open, total }
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState('');

  React.useEffect(() => {
    if (!from) { setCounts(null); return; }
    let live = true;
    Promise.all([
      api.get('/tasks', { params: { assigned_to: from } }),
      api.get('/tasks', { params: { assigned_to: from, open: 'true' } }),
    ]).then(([all, open]) => { if (live) setCounts({ total: all.data.length, open: open.data.length }); })
      .catch(() => { if (live) setCounts(null); });
    return () => { live = false; };
  }, [from]);

  const go = async () => {
    if (!from || !to) { setMsg('Pick both people'); return; }
    if (from === to)  { setMsg('Choose a different person to take over'); return; }
    setBusy(true); setMsg('');
    try {
      const r = await api.post('/tasks/reassign', { from_user_id: Number(from), to_user_id: Number(to), scope });
      setMsg(`✓ Reassigned ${r.data.reassigned} task(s)`);
      setTimeout(onDone, 900);
    } catch (e) { setMsg(e.response?.data?.error || 'Reassign failed'); }
    finally { setBusy(false); }
  };

  const willMove = counts ? (scope === 'open' ? counts.open : counts.total) : null;

  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && onClose()}>
      <div className="modal" style={{ maxWidth:480 }}>
        <div className="modal-header"><span className="modal-title">⇄ Reassign Tasks</span>
          <button className="modal-close" onClick={onClose}>×</button></div>
        <p style={{ fontSize:13, color:'var(--text2)' }}>Move one person's tasks to another — e.g. when someone leaves the organisation.</p>
        <div className="form-group"><label>Person leaving (reassign from)</label>
          <select value={from} onChange={e => setFrom(e.target.value)}>
            <option value="">— select —</option>
            {itUsers.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
          </select></div>
        {counts && <div style={{ fontSize:12.5, color:'var(--text2)', marginTop:-6, marginBottom:10 }}>
          Has <b style={{ color:'var(--text)' }}>{counts.open}</b> open · {counts.total} total assigned.</div>}
        <div className="form-group"><label>Take over (reassign to)</label>
          <select value={to} onChange={e => setTo(e.target.value)}>
            <option value="">— select —</option>
            {itUsers.filter(u => String(u.id) !== from).map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
          </select></div>
        <div className="form-group"><label>Which tasks</label>
          <select value={scope} onChange={e => setScope(e.target.value)}>
            <option value="open">Only open tasks (not Completed/Closed)</option>
            <option value="all">All tasks (including completed history)</option>
          </select></div>
        {willMove != null && <div style={{ fontSize:13, marginBottom:8 }}>This will move <b>{willMove}</b> task(s).</div>}
        {msg && <div style={{ fontSize:13, color: msg.startsWith('✓')?'var(--green)':'var(--red)' }}>{msg}</div>}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={busy || !from || !to} onClick={go}>{busy?'Reassigning…':'Reassign'}</button>
        </div>
      </div>
    </div>
  );
}
